import { Group, Mesh, MeshStandardMaterial, PlaneGeometry, Vector3 } from 'three';
import type { Streetscape } from '../../content/routes';
import { Rng } from '../../core/rng';
import { flatMaterial } from '../../render/materials/cache';
import { repeating } from '../../render/materials/canvasTexture';
import { crossSignalAt, signalAt } from '../../systems/junctions/signals';
import { StreetSide } from '../buildings/streetside';
import { Billboard } from '../props/billboards';
import { LAMP_HEADS } from '../props/propGeometries';
import type { Placement } from '../props/propField';
import type { StreetProps } from '../props/streetProps';
import { stationsBetween } from '../fuelStations';
import { activeRoute, tripAt, type TripPosition } from '../route';
import { JUNCTION_GAP, buildJunction } from './junctionPieces';
import { buildEdges, type Edges } from './roadEdges';
import { LAYOUT } from './roadLayout';
import { kerbTexture, medianTexture, roadTexture } from './roadTextures';
import type { TrafficLights } from './trafficLights';

export const TILE_COUNT = 12;
const TILES_BEHIND = 1;
/** Keep stalls and trees off the pavement this close to a bus stop, so the crowd has room. */
const STOP_CLEAR = 14;

function nearStop(worldZ: number): boolean {
  const { r } = tripAt(-worldZ);
  return activeRoute().stops.some((stop) => Math.abs(stop.at - r) < STOP_CLEAR);
}

/** Junction sequence number if the tile centred at `centre` holds a junction, else null. */
function junctionSeqIn(centre: TripPosition): number | null {
  const { junctions } = activeRoute();
  const index = junctions.findIndex((j) => Math.abs(j.at - centre.r) < 1);
  if (index === -1) return null;
  const inDrivingOrder = centre.dir === 'out' ? index : junctions.length - 1 - index;
  return centre.trip * junctions.length + inDrivingOrder;
}

/** The stretch of this tile (tile-local metres along the driving direction) that a petrol station occupies, if any. */
function stationPlot(startS: number): { from: number; to: number } | null {
  const station = stationsBetween(startS, startS + LAYOUT.tileLength)[0];
  if (!station) return null;
  const from = Math.max(0, station.position - 13 - startS);
  const to = Math.min(LAYOUT.tileLength, station.position + 13 - startS);
  return to - from > 1 ? { from, to } : null;
}

function legAtRoute(r: number) {
  const { legs } = activeRoute();
  return legs.find((leg) => r >= leg.start && r < leg.end) ?? legs[legs.length - 1]!;
}

interface Tile {
  group: Group;
  index: number;
  right: StreetSide;
  left: StreetSide;
  full: Edges;
  split: Edges;
  /** The tile at the end of each trip: a gap in the median for the U-turn. */
  uturn: Edges;
  junction: Group;
  lights: TrafficLights;
  junctionSeq: number | null;
  billboard: Billboard;
}

function roadStrip(width: number, material: MeshStandardMaterial, x: number, y: number): Mesh {
  const mesh = new Mesh(new PlaneGeometry(width, LAYOUT.tileLength), material);
  mesh.rotation.x = -Math.PI / 2;
  mesh.position.set(x, y, -LAYOUT.tileLength / 2);
  mesh.receiveShadow = true;
  return mesh;
}

function buildTile(road: MeshStandardMaterial, edgeMaterials: Parameters<typeof buildEdges>[1]): Tile {
  const group = new Group();
  group.add(roadStrip(LAYOUT.roadHalf * 2, road, 0, 0), roadStrip(LAYOUT.oppositeRoadWidth, road, LAYOUT.oppositeRoadX, 0), roadStrip(160, flatMaterial('#8d7458'), 0, -0.3));
  const full = buildEdges([{ from: 0, to: LAYOUT.tileLength }], edgeMaterials);
  const split = buildEdges([{ from: 0, to: JUNCTION_GAP.from }, { from: JUNCTION_GAP.to, to: LAYOUT.tileLength }], edgeMaterials);
  const whole = [{ from: 0, to: LAYOUT.tileLength }];
  const uturn = buildEdges(whole, edgeMaterials, [{ from: 0, to: U_TURN_GAP.from }, { from: U_TURN_GAP.to, to: LAYOUT.tileLength }]);
  const junction = buildJunction();
  const right = new StreetSide(1, LAYOUT.buildingFrontX, LAYOUT.sidewalkX);
  const left = new StreetSide(-1, LAYOUT.leftBuildingFrontX, LAYOUT.leftSidewalkX);
  const billboard = new Billboard();
  group.add(full.group, split.group, uturn.group, junction.group, right.group, left.group, billboard.group);
  return { group, index: Number.NaN, right, left, full, split, uturn, junction: junction.group, lights: junction.lights, junctionSeq: null, billboard };
}

/** The stretch of the last tile of a trip where the median is open, tile-local metres. */
const U_TURN_GAP = { from: 4, to: 28 } as const;

function inTurnGap(startS: number, z: number): boolean {
  const isTurnTile = startS >= 0 && (startS + LAYOUT.tileLength) % activeRoute().length === 0;
  return isTurnTile && z > U_TURN_GAP.from - 1 && z < U_TURN_GAP.to + 1;
}

/** A ring of road tiles that leapfrog forward as the trotro drives. */
export class RoadTiles {
  readonly group = new Group();
  private readonly tiles: Tile[] = [];
  private readonly road: MeshStandardMaterial;
  /** World positions of every street-lamp head in view, for the night glow. */
  private readonly lamps: Vector3[][] = [];
  private lampList: Vector3[] = [];

  constructor(private readonly props: StreetProps) {
    const road = new MeshStandardMaterial({ map: repeating(roadTexture(), 1, LAYOUT.tileLength / 20) });
    this.road = road;
    const edges = { kerb: new MeshStandardMaterial({ map: kerbTexture() }), median: new MeshStandardMaterial({ map: medianTexture() }) };
    for (let i = 0; i < TILE_COUNT; i += 1) {
      const tile = buildTile(road, edges);
      this.tiles.push(tile);
      this.group.add(tile.group);
    }
  }

  /** A wet road is darker and shinier (0 dry, 1 soaked). */
  setWet(wet: number): void {
    this.road.roughness = 1 - 0.62 * wet;
    this.road.color.setScalar(1 - 0.3 * wet);
  }

  lampPositions(): Vector3[] {
    return this.lampList;
  }

  /** Forget all dressing (after changing route). */
  invalidate(): void {
    for (const tile of this.tiles) tile.index = Number.NaN;
  }

  update(busS: number, time: number): void {
    const first = Math.floor(busS / LAYOUT.tileLength) - TILES_BEHIND;
    for (let offset = 0; offset < TILE_COUNT; offset += 1) {
      const index = first + offset;
      const slot = ((index % TILE_COUNT) + TILE_COUNT) % TILE_COUNT;
      const tile = this.tiles[slot];
      if (!tile) continue;
      if (tile.index !== index) this.dress(tile, slot, index);
      if (tile.junctionSeq !== null) tile.lights.show(signalAt(tile.junctionSeq, time), crossSignalAt(tile.junctionSeq, time));
    }
  }

  private dress(tile: Tile, slot: number, index: number): void {
    tile.index = index;
    const startS = index * LAYOUT.tileLength;
    tile.group.position.z = -startS;
    const centre = tripAt(Math.max(0, startS) + LAYOUT.tileLength / 2);
    const leg = legAtRoute(centre.r);
    const scape = leg.streetscape;
    tile.junctionSeq = startS >= 0 ? junctionSeqIn(centre) : null;
    const hasJunction = tile.junctionSeq !== null;
    const gap = hasJunction ? JUNCTION_GAP : null;
    tile.junction.visible = hasJunction;
    const turnTile = startS >= 0 && (startS + LAYOUT.tileLength) % activeRoute().length === 0;
    tile.full.group.visible = !hasJunction && !turnTile;
    tile.split.group.visible = hasJunction;
    tile.uturn.group.visible = turnTile;
    const edges = hasJunction ? tile.split : turnTile ? tile.uturn : tile.full;
    edges.barrierMedian.visible = scape.median === 'barrier';
    edges.grassMedian.visible = scape.median === 'grass';

    // Seeds follow the physical road, not the driving direction: coming back, the buildings that were
    // across the median are now on the kerb side, and the street runs the other way along the tile.
    const reverse = centre.dir === 'back';
    const routeTile = Math.floor(centre.r / LAYOUT.tileLength);
    const northSeed = routeTile * 2;
    const southSeed = routeTile * 2 + 1;
    // A petrol station takes a plot on the kerb side, so the buildings step aside for it.
    const stationGap = hasJunction ? null : stationPlot(startS);
    const rightGap = gap ?? stationGap;
    const right = tile.right.dress(reverse ? northSeed : southSeed, LAYOUT.tileLength, scape, leg.id, rightGap, reverse);
    const left = tile.left.dress(reverse ? southSeed : northSeed, LAYOUT.tileLength, scape, leg.id, gap, reverse);
    const rng = new Rng(index * 7 + 3);
    const billboardSide = rng.next() > 0.5 ? LAYOUT.sidewalkX + 1 : LAYOUT.leftSidewalkX - 1;
    tile.billboard.dress(index, billboardSide, !hasJunction && rng.next() < 0.35 && !(stationGap && billboardSide > 0));
    this.placeProps(slot, startS, scape, rng, gap, rightGap, right, left);
  }

  private placeProps(
    slot: number, startS: number, scape: Streetscape, rng: Rng, gap: typeof JUNCTION_GAP | null, rightGap: { from: number; to: number } | null,
    right: ReturnType<StreetSide['dress']>, left: ReturnType<StreetSide['dress']>,
  ): void {
    const outside = (g: { from: number; to: number } | null) => (p: Placement): boolean => g === null || !(-p.z > g.from - 2 && -p.z < g.to + 2);
    const world = (list: Placement[], g: { from: number; to: number } | null): Placement[] => list.filter(outside(g)).map((p) => ({ ...p, z: p.z - startS }));
    const sides = (pick: (s: ReturnType<StreetSide['dress']>) => Placement[]): Placement[] => [...world(pick(right), rightGap), ...world(pick(left), gap)];
    const kerbSideClear = (p: Placement): boolean => p.x < 0 || !nearStop(p.z);
    const palms: Placement[] = [];
    if (scape.medianPalms) {
      for (let z = 6; z < LAYOUT.tileLength; z += 13) if (!inTurnGap(startS, z)) palms.push({ x: LAYOUT.medianX, y: 0.25, z: -z, rotY: rng.range(0, 6), scale: rng.range(0.9, 1.1) });
    }
    const lights: Placement[] = scape.lights && !gap && !inTurnGap(startS, 20) ? [{ x: LAYOUT.medianX, y: 0.25, z: -20 }] : [];
    this.props.trees.setTile(slot, sides((s) => s.trees).filter(kerbSideClear));
    this.props.tanks.setTile(slot, sides((s) => s.tanks));
    this.props.umbrellas.setTile(slot, sides((s) => s.umbrellas).filter(kerbSideClear));
    this.props.palms.setTile(slot, world(palms, gap));
    const placed = world(lights, gap);
    this.props.lights.setTile(slot, placed);
    this.lamps[slot] = placed.flatMap((p) => LAMP_HEADS.map((head) => new Vector3(p.x + head.x, p.y + head.y, p.z)));
    this.lampList = this.lamps.flat();
  }
}
